import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Loader2, AlertCircle, Copy, Save, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InwardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [subRacks, setSubRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Editable per-item data: { [item.id]: { sale_rate, display_rate, sub_rack_id } }
  const [itemEdits, setItemEdits] = useState({});

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [detailRes, racksRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/inward/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/racks/sub-racks/all`, getAuthHeaders()),
        ]);
        const data = detailRes.data;
        setEntry(data);
        setSubRacks(racksRes.data);
        // Initialize edits from existing item data
        const edits = {};
        (data.items || []).forEach(item => {
          edits[item.id] = {
            sale_rate: item.sale_rate ?? '',
            display_rate: item.display_rate ?? '',
            sub_rack_id: item.sub_rack_id ?? '',
          };
        });
        setItemEdits(edits);
      } catch (err) {
        setError('Failed to fetch inward details');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const statusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'bg-green-100 text-green-700';
      case 'sold': return 'bg-blue-100 text-blue-700';
      case 'rented': return 'bg-purple-100 text-purple-700';
      case 'servicing': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const updateItemField = (itemId, field, value) => {
    setItemEdits(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  const copyToAll = (sourceItemId) => {
    const source = itemEdits[sourceItemId];
    if (!source) return;
    setItemEdits(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        next[key] = { ...source };
      });
      return next;
    });
  };

  const handleSaveBatch = async () => {
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    try {
      const items = Object.entries(itemEdits).map(([itemId, data]) => ({
        item_db_id: itemId,
        sale_rate: data.sale_rate !== '' ? parseFloat(data.sale_rate) : null,
        display_rate: data.display_rate !== '' ? parseFloat(data.display_rate) : null,
        sub_rack_id: data.sub_rack_id || null,
      }));
      const res = await axios.put(
        `${BACKEND_URL}/api/inward/${id}/save-batch`,
        { items },
        getAuthHeaders()
      );
      setEntry(res.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  const isComplete = entry?.batch_status === 'Complete';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error && !entry) {
    return (
      <div data-testid="inward-detail-error">
        <button onClick={() => navigate('/admin/inward')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Inward
        </button>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
          <AlertCircle className="h-5 w-5" />
          <span>{error || 'Entry not found'}</span>
        </div>
      </div>
    );
  }

  if (!entry) return null;

  return (
    <div data-testid="inward-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/inward')}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Inward
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inward Details</h1>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold uppercase tracking-wider ${
              isComplete ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-amber-100 text-amber-700 border border-amber-200'
            }`} data-testid="batch-status-badge">
              {isComplete && <CheckCircle className="h-3.5 w-3.5" />}
              {isComplete ? 'Complete' : 'Pending'}
            </span>
          </div>
          <p className="text-slate-600 mt-1 font-mono text-lg">{entry.batch_number}</p>
        </div>
        <div className="text-sm text-slate-500">
          Created: {formatDateTime(entry.created_at)}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Success */}
      {saveSuccess && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-sm mb-6" data-testid="save-success-msg">
          <CheckCircle className="h-5 w-5" />
          <span>Batch saved successfully! Inventory updated and batch marked as Complete.</span>
        </div>
      )}

      {/* Batch Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <InfoCard label="Product" value={entry.product_name} sub={entry.product_code} />
        <InfoCard label="Batch Number" value={entry.batch_number} mono />
        <InfoCard label="Quantity" value={entry.quantity} />
        <InfoCard label="Vendor" value={entry.vendor_name || '-'} />
        <InfoCard label="Inward Firm" value={entry.firm_name || '-'} />
        <InfoCard label="Tax" value={entry.tax_name ? `${entry.tax_name} (${entry.tax_percentage}%)` : '-'} />
        <InfoCard label="Purchase Rate" value={`₹${entry.purchase_rate?.toLocaleString()}`} />
        <InfoCard label="Invoice Date" value={formatDate(entry.invoice_date)} />
        <InfoCard label="Invoice Number" value={entry.invoice_number || '-'} />
        <InfoCard label="Payment Terms" value={entry.payment_term_name || '-'} />
        <InfoCard label="Sale Rate" value={entry.sale_rate ? `₹${entry.sale_rate.toLocaleString()}` : '-'} />
        <InfoCard label="Display Rate" value={entry.display_rate ? `₹${entry.display_rate.toLocaleString()}` : '-'} highlight />
      </div>

      {/* Items Section */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
            Individual Items
          </h2>
          <span className="text-sm text-slate-500">
            {entry.items?.length || 0} item{(entry.items?.length || 0) !== 1 ? 's' : ''}
          </span>
        </div>

        {!entry.items || entry.items.length === 0 ? (
          <div className="text-center py-12" data-testid="no-items">
            <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No individual items generated for this batch</p>
            <p className="text-slate-400 text-sm mt-1">Item generation was turned off during inward</p>
          </div>
        ) : (
          <>
            {/* Items Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-6 py-3 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">#</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Item ID</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Sale Rate (₹)</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Display Rate (₹)</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-3">Rack / Sub-Rack</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Actions</div>
            </div>

            {/* Items Rows */}
            {entry.items.map((item, idx) => {
              const edit = itemEdits[item.id] || { sale_rate: '', display_rate: '', sub_rack_id: '' };
              return (
                <div
                  key={item.id}
                  data-testid={`item-row-${item.item_id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 px-6 py-3 border-b border-slate-100 hover:bg-slate-50 items-center"
                >
                  <div className="text-slate-400 text-sm">{idx + 1}</div>
                  <div className="col-span-2">
                    <span className="font-mono font-medium text-slate-900 text-sm">{item.item_id}</span>
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor(item.status)}`}>
                      {item.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={edit.sale_rate}
                      onChange={e => updateItemField(item.id, 'sale_rate', e.target.value)}
                      placeholder="Sale Rate"
                      step="0.01"
                      min="0"
                      data-testid={`item-sale-rate-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={edit.display_rate}
                      onChange={e => updateItemField(item.id, 'display_rate', e.target.value)}
                      placeholder="Display Rate"
                      step="0.01"
                      min="0"
                      data-testid={`item-display-rate-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-3">
                    <select
                      value={edit.sub_rack_id}
                      onChange={e => updateItemField(item.id, 'sub_rack_id', e.target.value)}
                      data-testid={`item-sub-rack-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none"
                    >
                      <option value="">-- Select Sub-Rack --</option>
                      {subRacks.map(sr => (
                        <option key={sr.id} value={sr.id}>{sr.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => copyToAll(item.id)}
                      data-testid={`copy-to-all-${idx}`}
                      title="Copy to all items"
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-orange-600 border border-slate-200 px-2 py-1.5 rounded-sm hover:bg-orange-50 transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      <span className="hidden lg:inline">Copy All</span>
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Save Batch Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                {isComplete
                  ? 'This batch has been saved. You can still update and re-save.'
                  : 'Fill in sale rate, display rate, and rack assignment for each item, then save.'}
              </p>
              <button
                onClick={handleSaveBatch}
                disabled={saving}
                data-testid="save-batch-btn"
                className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-6 py-2.5 rounded-none hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Batch
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const InfoCard = ({ label, value, sub, mono, highlight }) => (
  <div className="bg-white rounded-sm border border-slate-200 p-4">
    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-base font-medium ${highlight ? 'text-orange-600' : 'text-slate-900'} ${mono ? 'font-mono' : ''}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-slate-400 font-mono mt-0.5">{sub}</div>}
  </div>
);
